import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  FilterBar,
  FilterChips,
  Pagination,
  FormActions,
  FormGrid,
  IconButton,
  Pill,
  SelectField,
  TextField,
  TelefoneField,
  formatCnpj,
  type Column,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useDebouncedValue } from "../../lib/use-debounce";
import { GRADE_FILTROS, SITUACAO_CADASTRO_OPCOES } from "../../lib/filtro-cadastros";
import { campoEditavel, oficiaisDiferem, readOnlyInputStyle, snapshotOficial } from "../../lib/edicao-manual";
import { CadastroPessoas } from "./cadastro-pessoas";
import { ConfirmarRecusaModal } from "../../components/ConfirmarRecusaModal";
import { ConfirmarEdicaoManualModal } from "../../components/ConfirmarEdicaoManualModal";
import { AcessoMaster } from "../../components/AcessoMaster";
import { AcoesCadastroPendente } from "../../components/AcoesCadastroPendente";
import { DocumentosCadastro, DocumentosPill } from "../../components/DocumentosCadastro";
import type { AdminBanco, AdminBancoInput, AdminVerbaCatalogoInput } from "@atlas/sdk";
import { NATUREZA_A_DEFINIR, opcoesNaturezaJuridica } from "../../lib/natureza-juridica";
import { OPCOES_MODO_PORTAL, rotuloModoPortal } from "../../lib/modo-portal";
import { avisaArquivoPorEmail, modoPortalSugerido } from "@atlas/domain";

const OPCOES_NATUREZA_JURIDICA = opcoesNaturezaJuridica(NATUREZA_A_DEFINIR);

// Esteira do site institucional — mesma mecanica de /averbadora/prefeituras:
// pendente vira ativo pela aprovacao, ou some pela recusa (hard delete).
// Os tres modos do modal seguem a nota em prefeituras.tsx: visualizar (olho da
// tabela) trava tudo, editar (lapis) destrava tudo, aprovar mantem a trava com
// confirmacao.
type ModoModal = "editar" | "aprovar" | "visualizar";

// formatCnpj + formatTelefone agora vem de @atlas/ui/web (compartilhado).
// readOnlyInputStyle + campoOficial vem de lib/edicao-manual (mesmo helper que
// prefeituras.tsx usa — o estilo era duplicado nos dois arquivos).

export function AdminBancos() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  // Credor de teste sai da lista por padrao (par do item 9, para bancos).
  const [incluirTeste, setIncluirTeste] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // Sem debounce cada tecla e uma queryKey nova, ou seja um request, e a tela
  // ficaria pior que a versao client-side que substitui.
  const buscaDebounced = useDebouncedValue(busca);

  // A busca e o filtro sobem para a rota junto com o recorte. Subir o recorte
  // sozinho faria a busca procurar apenas dentro da pagina: o operador digita
  // uma razao social que existe e a tela diz que nao existe, sem erro nenhum.
  const data = useQuery({
    queryKey: ["admin", "bancos", buscaDebounced, situacao, incluirTeste, pagina, porPagina],
    queryFn: () => atlas.admin.listBancos({
      q: buscaDebounced || undefined,
      status: situacao || undefined,
      incluirTeste: incluirTeste ? "1" : undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  /** Mexer num filtro volta pra primeira pagina — a posicao antiga cairia num
   *  offset que o novo recorte pode nem ter. (Trocar o TAMANHO da pagina nao
   *  precisa disto: o `Pagination` ja chama `onPagina(1)` por dentro.) */
  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };
  const testar = useMutation({
    mutationFn: (id: number) => atlas.admin.testarBanco(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "bancos"] }),
  });
  // Nunca exclui — desativa/reativa (status). Reversível, sem perda de dados.
  const toggleAtivo = useMutation({
    mutationFn: (b: AdminBanco) => atlas.admin.upsertBanco({
      id: b.id, nome: b.nome, status: b.status === "inativo" ? "ativo" : "inativo",
      adapter: b.adapter, contatoEmail: b.contatoEmail, loginEmail: b.loginEmail,
      scopes: b.scopes, mtlsHabilitado: b.mtlsHabilitado,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "bancos"] }),
  });
  // #203: parceiro -> consignataria sem acesso. So' nesse sentido; o inverso e'
  // cadastro novo de acesso, com login e escopos escolhidos.
  const converter = useMutation({
    mutationFn: ({ id, justificativa }: { id: number; justificativa: string }) =>
      atlas.admin.converterEmConsignataria(id, justificativa),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin", "bancos"] });
      alert(
        `${r.banco.nome} agora é credor sem acesso. ` +
          `Convênios arquivados: ${r.convenios.length}; operadores desativados: ${r.operadores}; tokens revogados: ${r.tokens}. ` +
          "Os contratos continuam no movimento.",
      );
    },
    onError: (e) => alert(e instanceof Error ? e.message : "Não foi possível converter."),
  });
  const pedirConversao = (b: AdminBanco) => {
    const justificativa = window.prompt(
      `Converter "${b.nome}" em credor sem acesso?\n\n` +
        "O login, os operadores e os tokens de API deixam de funcionar, e os convênios ficam arquivados. " +
        "Os contratos continuam no movimento.\n\nPor que está convertendo?",
      "",
    );
    if (justificativa === null) return;
    if (justificativa.trim().length < 10) {
      alert("Escreva a justificativa (ao menos 10 caracteres): ela fica na auditoria.");
      return;
    }
    converter.mutate({ id: b.id, justificativa: justificativa.trim() });
  };
  const [editing, setEditing] = useState<{ banco: AdminBanco | null; modo: ModoModal } | null>(null);
  const [recusando, setRecusando] = useState<AdminBanco | null>(null);
  const recusar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => atlas.admin.recusarCadastroBanco(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "bancos"] });
      // Badge do menu decrementa junto com a recusa.
      qc.invalidateQueries({ queryKey: ["admin", "cadastros-pendentes"] });
    },
  });

  // A pagina JA vem fatiada, filtrada e ordenada (pendentes no topo, resto
  // A-Z) — `filtrarOrdenarCadastros` saiu daqui na 007 e a ordem virou
  // `ORDEM_CADASTRO` em db/consultas-averbadora.ts. `recorteDoServidor` da a
  // MESMA forma que o recorte no cliente, e e o que deixa a diferenca entre
  // cliente e servidor invisivel para o `Pagination`.
  const daPagina = recorteDoServidor(data.data?.bancos ?? [], data.data?.total ?? 0, pagina, porPagina);
  const temFiltro = Boolean(busca || situacao || incluirTeste);

  // Colunas espelham CSV exemplo (nome, status, adapter, contatoEmail,
  // loginEmail, acesso do master) + extras uteis do fluxo CNPJ (CNPJ, telefone).
  const columns: Column<AdminBanco>[] = [
    {
      key: "status",
      header: "Status",
      // "pendente" tambem cai no amarelo — antes caia no `expirado` (vermelho),
      // o que fazia cadastro do site parecer erro em vez de fila de aprovacao.
      render: (b) => <Pill variant={b.status === "ativo" ? "averbado" : b.status === "pausado" || b.status === "pendente" ? "pendente" : "expirado"}>{b.status}</Pill>,
    },
    { key: "nome", header: "Consignatária" },
    // #313: quem opera benefícios no portal, à vista da averbadora.
    {
      key: "modoPortal",
      header: "Operação",
      render: (b) => <Pill variant={(b.modoPortal ?? "credito") === "credito" ? "emdia" : "aceita"}>{rotuloModoPortal(b.modoPortal)}</Pill>,
    },
    {
      key: "cnpj",
      header: "CNPJ",
      mono: true,
      render: (b) => b.cnpj ? formatCnpj(b.cnpj) : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    { key: "adapter", header: "Adapter" },
    { key: "contatoEmail", header: "Contato" },
    {
      key: "acesso",
      header: "Login",
      render: (b) => b.loginEmail
        ? <span style={{ color: "var(--emerald-500)" }}>{b.loginEmail}</span>
        : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    {
      key: "senha",
      header: "Acesso",
      render: (b) => !b.loginEmail
        ? <span style={{ color: "var(--text-dim)" }}>—</span>
        : b.hasPassword
          ? <span style={{ color: "var(--emerald-500)" }} title="O master já criou a senha no primeiro acesso">✓ ativado</span>
          : <span style={{ color: "var(--gold-500)" }} title="O master ainda não fez o primeiro acesso">aguardando primeiro acesso</span>,
    },
    {
      key: "telefone",
      header: "Telefone",
      render: (b) => b.telefone || <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    // Ver a nota igual em prefeituras.tsx.
    {
      key: "documentos",
      header: "Documentos",
      render: (b) => <DocumentosPill docFrenteKey={b.docFrenteKey} docVersoKey={b.docVersoKey} selfieKey={b.selfieKey} />,
    },
    { key: "scopes", header: "Scopes", render: (b) => b.scopes.join(", ") || "—" },
    { key: "mtlsHabilitado", header: "mTLS", render: (b) => (b.mtlsHabilitado ? "sim" : "não") },
    {
      key: "teste",
      header: "Última conexão",
      render: (b) =>
        b.ultimoTeste ? (
          <span style={{ color: b.ultimoTesteOk ? "var(--emerald-500)" : "var(--danger-500)" }}>
            {b.ultimoTesteOk ? "OK" : "FALHA"} • {new Date(b.ultimoTeste).toLocaleString("pt-BR")}
          </span>
        ) : "—",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Consignatárias</h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Botao "Limpar Base" removido (mesmo motivo de /prefeituras) */}
          <Button onClick={() => setEditing({ banco: null, modo: "editar" })}>+ Adicionar consignatária</Button>
        </div>
      </header>

      {/* CsvImportPanel removido a pedido do cliente (17/07/2026) — cadastro
          agora e SEMPRE via CNPJ (BrasilAPI + fallback), mesmo padrao das
          prefeituras. CSV importava linhas EXEMPLO como reais. */}

      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por CNPJ ou razão social"
        onReset={() => { setBusca(""); setSituacao(""); setIncluirTeste(false); setPagina(1); }}
      >
        <div style={GRADE_FILTROS}>
          {/* SEM CONTAGEM de proposito: o filtro vai pra API (esta na queryKey)
              e o numero teria que sair da base inteira, que a tela nao tem.
              Chip sem numero continua sendo filtro de um clique; chip com
              numero errado seria pior que chip sem numero. */}
          <FilterChips
            label="Situação"
            value={situacao}
            onChange={filtrar(setSituacao)}
            options={SITUACAO_CADASTRO_OPCOES}
          />
          {/* Nao e' "situacao": todo valor nao-ativo de `status` barra o LOGIN.
              Eixos diferentes — status e' acesso, este e' contagem. */}
          <SelectField
            label="Credores de teste"
            value={incluirTeste ? "1" : ""}
            onChange={(e) => { setIncluirTeste(e.target.value === "1"); setPagina(1); }}
            options={[
              { value: "", label: "Esconder (padrão)" },
              { value: "1", label: "Mostrar junto" },
            ]}
          />
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(b) => String(b.id)}
        loading={data.isLoading}
        // Distinguir "nada bate com o filtro" de "nao ha cadastro": lido como
        // base vazia, o operador vai procurar erro onde nao ha.
        emptyState={temFiltro
          ? "Nenhuma consignatária bate com esses filtros. Limpe os filtros para ver o resto do cadastro."
          : "Nenhuma consignatária cadastrada. Clique em '+ Adicionar consignatária' pra começar."}
        actions={(b) => (
          // Pendente = cadastro do site institucional aguardando esteira. Testar
          // conexao/Desativar so fazem sentido depois de aprovado.
          b.status === "pendente" ? (
            <AcoesCadastroPendente
              onAceitar={() => setEditing({ banco: b, modo: "aprovar" })}
              onRecusar={() => setRecusando(b)}
            />
          ) : (
            <>
              <IconButton title="Visualizar" onClick={() => setEditing({ banco: b, modo: "visualizar" })}>👁</IconButton>
              <IconButton title="Editar" onClick={() => setEditing({ banco: b, modo: "editar" })}>✎</IconButton>
              <IconButton title="Testar conexão" onClick={() => testar.mutate(b.id)}>↻</IconButton>
              {b.tipo !== "consignataria" ? (
                <IconButton title="Converter em credor sem acesso" onClick={() => pedirConversao(b)}>⇄</IconButton>
              ) : null}
              {b.status === "inativo" ? (
                <IconButton title="Reativar" onClick={() => toggleAtivo.mutate(b)}>▶</IconButton>
              ) : (
                <IconButton danger title="Desativar" onClick={() => { if (confirm(`Desativar a consignatária "${b.nome}"?\n\nEla para de operar, mas os dados não são apagados — você pode reativar depois.`)) toggleAtivo.mutate(b); }}>⏸</IconButton>
              )}
            </>
          )
        )}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="bancos"
      />

      {editing ? <BancoModal initial={editing.banco} modo={editing.modo} onClose={() => setEditing(null)} /> : null}
      {recusando ? (
        <ConfirmarRecusaModal
          titulo="Recusar cadastro de consignatária"
          alvo={`${recusando.nome}${recusando.cnpj ? ` — ${formatCnpj(recusando.cnpj)}` : ""}`}
          protocolo={recusando.protocoloCadastro}
          itensRemovidos={[
            "Dados da consignatária e credenciais de acesso",
            "Representantes legais cadastrados",
            "Responsáveis (financeiro e operacional)",
            "Usuário master",
          ]}
          onConfirm={(motivo) => recusar.mutateAsync({ id: recusando.id, motivo })}
          onClose={() => setRecusando(null)}
        />
      ) : null}
      {/* LimparBaseBancosModal removida 27/07/2026 — botao ja tinha sumido
          e a modal era orfa (state + componente sem trigger). Reintroduzir
          via novo botao se o fluxo voltar a ser necessario. */}
    </div>
  );
}

function BancoModal({ initial, modo = "editar", onClose }: { initial: AdminBanco | null; modo?: ModoModal; onClose: () => void }) {
  const aprovando = modo === "aprovar";
  const visualizando = modo === "visualizar";
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminBancoInput>({
    id: initial?.id,
    nome: initial?.nome ?? "",
    status: initial?.status ?? "ativo",
    adapter: initial?.adapter ?? "sandbox",
    contatoEmail: initial?.contatoEmail ?? "",
    loginEmail: initial?.loginEmail ?? "",
    scopes: initial?.scopes ?? ["propostas:rw"],
    mtlsHabilitado: initial?.mtlsHabilitado ?? false,
    baseUrl: initial?.baseUrl ?? "",
    cnpj: initial?.cnpj ?? "",
    razaoSocial: initial?.razaoSocial ?? "",
    nomeFantasia: initial?.nomeFantasia ?? "",
    dataFundacao: initial?.dataFundacao ?? "",
    atividade: initial?.atividade ?? "",
    telefone: initial?.telefone ?? "",
    endereco: initial?.endereco ?? {},
    nomeContato: initial?.nomeContato ?? "",
    agencia: initial?.agencia ?? "",
    dominiosEmail: initial?.dominiosEmail ?? [],
    tenantDeTeste: initial?.tenantDeTeste ?? false,
    naturezaJuridica: initial?.naturezaJuridica ?? undefined,
    modoPortal: initial?.modoPortal ?? "credito",
    antecedenciaAlertaDias: initial?.antecedenciaAlertaDias ?? 5,
    // #319: so' a escolha explicita viaja. `null` segue o modo — e continua
    // `null` ate' alguem marcar ou desmarcar a caixa.
    avisoArquivoEmail: initial?.avisoArquivoEmail ?? null,
  });
  // #313: no cadastro NOVO, escolher a natureza sugere o modo — até a
  // averbadora mexer no modo à mão. Editar nunca re-sugere.
  const [modoTocado, setModoTocado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fora do `form`: grava na hora, por rota propria, e nao viaja no upsert do cadastro.
  const [confirmacaoAutomatica, setConfirmacaoAutomatica] = useState(initial?.confirmacaoAutomatica ?? false);
  const definirConfirmacao = useMutation({
    mutationFn: (ativa: boolean) => atlas.admin.consignatarias.definirConfirmacaoAutomatica(initial!.id, ativa),
    onSuccess: (r) => {
      setConfirmacaoAutomatica(r.confirmacaoAutomatica);
      qc.invalidateQueries({ queryKey: ["admin", "bancos"] });
    },
  });
  const [cnpjInput, setCnpjInput] = useState<string>(initial?.cnpj ?? "");
  const [cnpjError, setCnpjError] = useState<string | null>(null);
  // Na aprovacao e na visualizacao ja abre expandido — o operador precisa ver tudo.
  const [avancado, setAvancado] = useState(aprovando || visualizando);
  // Aviso "Falta preencher" so aparece apos primeira tentativa de submit.
  const [triedSubmit, setTriedSubmit] = useState(false);
  // So no modo aprovar — mesma mecanica de prefeituras.tsx (ver lib/edicao-manual.ts).
  const [edicaoManual, setEdicaoManual] = useState(false);
  const [confirmandoEdicao, setConfirmandoEdicao] = useState(false);
  const camposLiberados = modo === "editar" || (aprovando && edicaoManual);
  // Ultima origem oficial conhecida — base do dirty-check que decide a flag
  // `dadosEditadosManualmente` no save. Ver a nota igual em prefeituras.tsx.
  const [baseOficial, setBaseOficial] = useState(() => snapshotOficial(initial));
  // Consulta CNPJ — mesmo endpoint das prefeituras.
  const consulta = useMutation({
    mutationFn: (cnpj: string) => atlas.admin.consultarCnpjPrefeitura(cnpj),
    onMutate: () => setCnpjError(null),
    onSuccess: (r) => {
      const d = r.dados;
      // Consulta nova sobrescreve tudo com dado oficial — volta a travar (so
      // tem efeito no modo aprovar).
      setEdicaoManual(false);
      const oficiais = {
        razaoSocial: (d.razao_social as string | undefined) ?? "",
        nomeFantasia: (d.nome_fantasia as string | undefined) ?? "",
        dataFundacao: (d.data_inicio_atividade as string | undefined) ?? "",
        atividade: (d.cnae_fiscal_descricao as string | undefined) ?? "",
        endereco: {
          logradouro: (d.logradouro as string | undefined) ?? "",
          numero: (d.numero as string | undefined) ?? "",
          complemento: (d.complemento as string | undefined) ?? "",
          bairro: (d.bairro as string | undefined) ?? "",
          cep: (d.cep as string | undefined) ?? "",
          municipio: (d.municipio as string | undefined) ?? "",
          uf: (d.uf as string | undefined) ?? "",
        },
      };
      // Nova referencia oficial pro dirty-check do save.
      setBaseOficial(snapshotOficial(oficiais));
      setForm((prev) => ({
        ...prev,
        ...oficiais,
        cnpj: (d.cnpj as string | undefined) ?? cnpjInput.replace(/\D/g, ""),
        nome: (d.nome_fantasia as string | undefined) ?? (d.razao_social as string | undefined) ?? prev.nome,
        // BrasilAPI/ReceitaWS as vezes retorna 2 telefones concatenados
        // ("(11) 2475-8641 / (011) 6440-1929"). Pega so o primeiro.
        telefone: ((d.ddd_telefone_1 as string | undefined) ?? "").split(/\s*\/\s*/)[0]?.trim() ?? "",
        contatoEmail: prev.contatoEmail || (d.email as string | undefined) || "",
      }));
    },
    onError: (err) => {
      const withDetails = err as { details?: { cnpj?: string }; message?: string; status?: number };
      const detalhe = withDetails.details?.cnpj;
      const msg = detalhe ?? withDetails.message ?? "Falha na consulta";
      if (withDetails.status === 404 || msg.includes("cnpj_nao_encontrado")) {
        setCnpjError("CNPJ não encontrado na Receita.");
      } else {
        setCnpjError(msg);
      }
    },
  });
  const buscarCnpj = () => {
    const digits = cnpjInput.replace(/\D/g, "");
    if (digits.length !== 14) {
      setCnpjError("CNPJ deve ter 14 dígitos.");
      return;
    }
    consulta.mutate(digits);
  };
  const dadosPreenchidos = !!(form.razaoSocial || form.nomeFantasia || form.dataFundacao || form.atividade || form.endereco?.logradouro);
  const save = useMutation({
    mutationFn: () => {
      // Aprovar = mesmo upsert com o status promovido pra ativo (sem rota nova).
      const payload: AdminBancoInput = {
        ...form,
        status: aprovando ? "ativo" : form.status,
        // Marca so quando um campo oficial saiu do valor oficial — ver a nota
        // igual em prefeituras.tsx.
        dadosEditadosManualmente:
          initial?.dadosEditadosManualmente
          || oficiaisDiferem(snapshotOficial(form), baseOficial)
          || undefined,
      };
      if (!payload.loginEmail) delete payload.loginEmail;
      if (!payload.cnpj) delete payload.cnpj;
      return atlas.admin.upsertBanco(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "bancos"] });
      // Aprovar tira o cadastro da fila — badge do menu acompanha.
      qc.invalidateQueries({ queryKey: ["admin", "cadastros-pendentes"] });
      onClose();
    },
    onError: (err) => {
      // Expande details.fieldErrors do Zod pra o usuario ver qual campo falhou.
      const e = err as { message?: string; details?: Record<string, unknown> };
      const det = e.details as { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | undefined;
      const campos: string[] = [];
      if (det?.fieldErrors) {
        for (const [k, msgs] of Object.entries(det.fieldErrors)) {
          if (msgs?.length) campos.push(`${k}: ${msgs.join(", ")}`);
        }
      }
      if (det && !det.fieldErrors) {
        for (const [k, v] of Object.entries(det)) {
          if (typeof v === "string") campos.push(`${k}: ${v}`);
        }
      }
      if (det?.formErrors?.length) campos.push(...det.formErrors);
      const base = e.message || "Erro ao salvar";
      setError(campos.length > 0 ? `${base} — ${campos.join(" | ")}` : base);
    },
  });

  const loginTrocado = !!initial?.loginEmail
    && (form.loginEmail ?? "").trim().toLowerCase() !== initial.loginEmail;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={modalBackdrop}
    >
      <div onMouseDown={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>
          {aprovando
            ? `Aprovar cadastro — ${initial?.nome ?? ""}`
            : visualizando
              ? `Visualizar ${initial?.nome ?? ""}`
              : initial ? `Editar ${initial.nome}` : "Nova consignatária"}
        </h3>

        {/* Banner do modo aprovacao — ver nota igual em prefeituras.tsx. */}
        {aprovando ? (
          <div style={{
            padding: 14, borderRadius: 10,
            background: "color-mix(in srgb, var(--emerald-500) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--emerald-500) 30%, transparent)",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--emerald-500)", textTransform: "uppercase", marginBottom: 6 }}>
              Cadastro pendente do site institucional
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Confira e corrija os dados abaixo — inclusive representantes legais, responsáveis
              e usuário master. Ao confirmar, o cadastro passa para <b style={{ color: "var(--text)" }}>ATIVO</b> e
              o master ativa o acesso em “Primeiro acesso”, com o e-mail de login e o código enviado a ele.
            </div>
          </div>
        ) : null}

        {/* PASSO 1 — Consulta CNPJ. Aparece TAMBEM em edicao: como os campos
            oficiais sao readOnly, sem este passo um banco cadastrado antes da
            consulta CNPJ ficava com razao social/endereco vazios pra sempre.
            Serve tambem pra reatualizar dados que mudaram na Receita. */}
        <div>
            <label style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
              CNPJ da consignatária
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={cnpjInput}
                onChange={(e) => setCnpjInput(formatCnpj(e.target.value))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarCnpj(); } }}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                readOnly={visualizando}
                style={{
                  flex: 1, padding: "10px 12px", borderRadius: 8,
                  border: "1px solid var(--border-strong)",
                  background: "var(--bg-elev)", color: "var(--text)",
                  fontSize: 14, fontFamily: "var(--font-mono)",
                  ...(visualizando ? readOnlyInputStyle : {}),
                }}
              />
              {/* Pesquisar sobrescreve o form inteiro — nao existe em visualizar. */}
              {!visualizando ? (
                <Button type="button" onClick={buscarCnpj} disabled={consulta.isPending}>
                  {consulta.isPending ? "Buscando…" : "Pesquisar"}
                </Button>
              ) : null}
            </div>
            {cnpjError ? <div style={{ color: "var(--danger-500)", fontSize: 12, marginTop: 6 }}>{cnpjError}</div> : null}
            {!visualizando && !cnpjError && !dadosPreenchidos ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Consulta pública (Receita + Junta Comercial). Preenche razão social, endereço, atividade e telefone automaticamente.
              </div>
            ) : null}
            {!visualizando && !cnpjError && dadosPreenchidos && initial ? (
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Pesquise novamente para reatualizar os dados oficiais pela Receita.
              </div>
            ) : null}
        </div>

        {/* PASSO 2 — Dados oficiais. Aparece sempre em edicao (mesmo vazio pra
            registros antigos sem o campo salvo). Read-only exceto telefone. */}
        {(dadosPreenchidos || initial) ? (
          <div style={{ padding: 14, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
            {/* Rotulo segue o estado real — ver nota igual em prefeituras.tsx.
                Terceiro estado: edicao manual habilitada (dourado). */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: edicaoManual ? "var(--gold-500)" : dadosPreenchidos ? "var(--emerald-500)" : "var(--warn-500)", textTransform: "uppercase" }}>
                {edicaoManual
                  ? "✎ Edição manual habilitada"
                  : dadosPreenchidos ? "✓ Dados oficiais preenchidos" : "⚠ Dados oficiais não preenchidos"}
              </div>
              {/* Valvula de escape SO na esteira de aprovacao — ver a nota
                  igual em prefeituras.tsx. */}
              {aprovando && !edicaoManual ? (
                <Button variant="ghost" type="button" onClick={() => setConfirmandoEdicao(true)} style={{ flexShrink: 0, fontSize: 12, padding: "6px 10px" }}>
                  ✎ Editar manualmente
                </Button>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
              {!dadosPreenchidos
                ? visualizando
                  ? "Este cadastro não tem os dados oficiais da Receita/Junta Comercial."
                  : "Este cadastro não tem os dados oficiais. Informe o CNPJ no campo acima e clique em Pesquisar para preencher."
                : edicaoManual
                  ? "Edição manual ativa — os dados abaixo podem ser alterados e deixam de refletir exatamente a Receita/Junta Comercial. Pesquisar o CNPJ novamente restaura os dados oficiais e volta a travar os campos."
                  : camposLiberados
                    ? "Provenientes da Receita/Junta Comercial. Alterar qualquer campo aqui marca o cadastro como editado manualmente."
                    : visualizando
                      ? "Somente leitura — provenientes da Receita/Junta Comercial."
                      : "Somente leitura — provenientes da Receita/Junta Comercial. Apenas o telefone pode ser editado."}
            </div>
            {!edicaoManual && initial?.dadosEditadosManualmente ? (
              <div style={{ fontSize: 11, color: "var(--gold-500)", marginBottom: 10 }}>
                ✎ Este cadastro já teve dados editados manualmente
                {initial.dadosEditadosManualmenteEm ? ` em ${new Date(initial.dadosEditadosManualmenteEm).toLocaleString("pt-BR")}` : ""}.
              </div>
            ) : null}
            <FormGrid cols={2}>
              {/* `nome` (coluna "Banco" da tabela, exigido na validacao) segue
                  nome fantasia com fallback pra razao social — mesma regra que a
                  consulta de CNPJ aplica. Sem isso, corrigir na mao deixava a
                  lista com o valor antigo. */}
              <TextField label="Razão social" {...campoEditavel(camposLiberados, form.razaoSocial ?? "", (v) => setForm({ ...form, razaoSocial: v, nome: form.nomeFantasia || v }))} />
              <TextField label="Nome fantasia" {...campoEditavel(camposLiberados, form.nomeFantasia ?? "", (v) => setForm({ ...form, nomeFantasia: v, nome: v || form.razaoSocial || "" }))} />
              <TextField label="Data de fundação" {...campoEditavel(camposLiberados, form.dataFundacao ?? "", (v) => setForm({ ...form, dataFundacao: v }))} hint={camposLiberados ? "Formato AAAA-MM-DD." : undefined} />
              <TextField label="Atividade principal" {...campoEditavel(camposLiberados, form.atividade ?? "", (v) => setForm({ ...form, atividade: v }))} />
              {/* Telefone fica FORA do campoEditavel — ver a nota em prefeituras.tsx. */}
              <TelefoneField
                label="Telefone"
                value={form.telefone ?? ""}
                onChange={visualizando ? () => undefined : (e) => setForm({ ...form, telefone: e.target.value })}
                readOnly={visualizando}
                style={visualizando ? readOnlyInputStyle : undefined}
                required
                hint={visualizando ? undefined : "Obrigatório — editável se o telefone oficial estiver desatualizado"}
              />
              <TextField label="CEP" {...campoEditavel(camposLiberados, form.endereco?.cep ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, cep: v } }))} />
              <TextField label="Logradouro" {...campoEditavel(camposLiberados, form.endereco?.logradouro ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, logradouro: v } }))} />
              <TextField label="Número" {...campoEditavel(camposLiberados, form.endereco?.numero ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, numero: v } }))} />
              <TextField label="Bairro" {...campoEditavel(camposLiberados, form.endereco?.bairro ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, bairro: v } }))} />
              <TextField label="Município" {...campoEditavel(camposLiberados, form.endereco?.municipio ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, municipio: v } }))} />
              <TextField label="UF" {...campoEditavel(camposLiberados, form.endereco?.uf ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, uf: v.slice(0, 2).toUpperCase() } }))} maxLength={2} />
              {/* Ja vinha no payload da Receita mas nao tinha campo na tela. */}
              <TextField label="Complemento" {...campoEditavel(camposLiberados, form.endereco?.complemento ?? "", (v) => setForm({ ...form, endereco: { ...form.endereco, complemento: v } }))} />
            </FormGrid>
            {/* Campos da modelagem do site que a averbadora PODE editar — nao
                vem da Receita, entao ficam fora do bloco readOnly acima. */}
            <div style={{ marginTop: 12 }}>
              <FormGrid cols={2}>
                <TextField
                  label="Nome do contato"
                  {...campoEditavel(!visualizando, form.nomeContato ?? "", (v) => setForm({ ...form, nomeContato: v }))}
                  hint="Pessoa de contato na consignatária."
                />
                <TextField
                  label="Agência"
                  {...campoEditavel(!visualizando, form.agencia ?? "", (v) => setForm({ ...form, agencia: v.replace(/\D/g, "").slice(0, 5) }))}
                  hint="Até 5 dígitos."
                />
                {/* `<select>` ignora readOnly — travar exige `disabled`. Vazio =
                    "A definir": credor anterior a 08/10/2026 (#269). Escolher grava;
                    deixar em "A definir" não manda o campo e a API mantém o atual. */}
                <SelectField
                  label="Natureza jurídica"
                  value={form.naturezaJuridica ?? ""}
                  disabled={visualizando}
                  onChange={(e) => {
                    const naturezaJuridica = (e.target.value || undefined) as AdminBancoInput["naturezaJuridica"];
                    setForm({
                      ...form,
                      naturezaJuridica,
                      ...(!initial && !modoTocado ? { modoPortal: modoPortalSugerido(naturezaJuridica) } : {}),
                    });
                  }}
                  options={OPCOES_NATUREZA_JURIDICA}
                  hint="O que a entidade é juridicamente. Ao cadastrar, sugere a operação no portal."
                />
                <SelectField
                  label="Operação no portal"
                  value={form.modoPortal ?? "credito"}
                  disabled={visualizando}
                  onChange={(e) => {
                    setModoTocado(true);
                    setForm({ ...form, modoPortal: e.target.value as AdminBancoInput["modoPortal"] });
                  }}
                  options={OPCOES_MODO_PORTAL}
                  hint="Benefícios: a consignatária vê e encerra as consignações facultativas dela e atende pedidos de cancelamento."
                />
                {/* #278: só a consignatária manda arquivo mensal. */}
                {initial?.tipo === "consignataria" ? (
                  <TextField
                    label="Avisar antes do corte (dias)"
                    type="number"
                    min={0}
                    max={28}
                    {...campoEditavel(!visualizando, String(form.antecedenciaAlertaDias ?? 5), (v) =>
                      setForm({ ...form, antecedenciaAlertaDias: Math.max(0, Math.min(28, Math.trunc(Number(v) || 0))) }),
                    )}
                    hint="Quantos dias antes do corte do órgão o alerta abre, quando o arquivo mensal ainda não foi confirmado. Na véspera, o aviso fica crítico."
                  />
                ) : null}
                {/* #319: marcada pelo valor EFETIVO — a escolha do cadastro ou o
                    padrão do modo. Mexer grava a escolha explícita. */}
                {initial?.tipo === "consignataria" ? (
                  <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer" }}>
                    <input
                      type="checkbox"
                      checked={avisaArquivoPorEmail({ aviso: form.avisoArquivoEmail, modoPortal: form.modoPortal })}
                      disabled={visualizando}
                      onChange={(e) => setForm({ ...form, avisoArquivoEmail: e.target.checked })}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <b style={{ color: "var(--text)" }}>Avisar a consignatária por e-mail</b>
                      <br />
                      Quando o alerta do arquivo mensal abre e na véspera do corte, uma vez cada, no e-mail de login e no
                      de contato. Ligado por padrão para quem opera benefícios. A averbadora vê o alerta de qualquer forma.
                    </span>
                  </label>
                ) : null}
                {initial?.protocoloCadastro ? (
                  <TextField label="Protocolo do cadastro" value={initial.protocoloCadastro} readOnly style={readOnlyInputStyle} onChange={() => undefined} hint="Gerado pelo cadastro no site institucional." />
                ) : null}
              </FormGrid>
              {/* Dominios de e-mail (coluna bancos.dominios_email). Editado como
                  texto separado por virgula e convertido pra array no onChange —
                  o dominio do e-mail de login a API acrescenta sozinha. */}
              <div style={{ marginTop: 12 }}>
                <TextField
                  label="Domínios de e-mail"
                  {...campoEditavel(!visualizando, (form.dominiosEmail ?? []).join(", "), (v) => setForm({
                    ...form,
                    dominiosEmail: v.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean),
                  }))}
                  placeholder="consignataria.com.br, grupo.com.br"
                  hint="Separe por vírgula. O domínio do e-mail de login entra automaticamente."
                />
              </div>
              {/* Pessoas do cadastro (tabelas da migration 0001). Salvam direto
                  na API, fora do "Salvar" do modal — ver cadastro-pessoas.tsx. */}
              <CadastroPessoas kind="bancos" tenantId={initial?.id} somenteLeitura={visualizando} />
            </div>
          </div>
        ) : null}

        {/* Ver a nota igual em prefeituras.tsx — aparece em visualizar e em
            aprovar, nunca em editar. */}
        {initial && (visualizando || aprovando) ? (
          <DocumentosCadastro
            docFrenteKey={initial.docFrenteKey}
            docVersoKey={initial.docVersoKey}
            selfieKey={initial.selfieKey}
            veioDoSite={!!initial.protocoloCadastro}
            carregar={(parte) => atlas.admin.bancoDocumentoBlob(initial.id, parte)}
          />
        ) : null}

        {/* PASSO 3 — Credenciais de acesso. So o e-mail: a senha nasce no primeiro acesso do master. */}
        <div style={{ padding: 14, background: "color-mix(in srgb, var(--accent) 6%, transparent)", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--accent)", textTransform: "uppercase", marginBottom: 10 }}>
            🔑 Credenciais de acesso da consignatária
          </div>
          <FormGrid cols={2}>
            <TextField
              label="E-mail de login"
              {...campoEditavel(!visualizando, form.loginEmail ?? "", (v) => setForm({ ...form, loginEmail: v }))}
              type="email"
              placeholder="operador@consignataria.com.br"
              required
              hint="O master ativa o acesso em “Primeiro acesso” com este e-mail e depois o usa em todo login."
              autoComplete="off"
              name="atlas-novo-banco-login-email"
            />
            <AcessoMaster hasPassword={!!initial?.hasPassword} loginTrocado={loginTrocado} />
            <TextField
              label="E-mail de contato do responsável"
              {...campoEditavel(!visualizando, form.contatoEmail, (v) => setForm({ ...form, contatoEmail: v }))}
              type="email"
              placeholder="responsavel@consignataria.com.br"
              required
            />
          </FormGrid>
        </div>

        {/* PASSO 4 — Opcoes avancadas (adapter, scopes, mTLS, situacao). */}
        <details open={avancado} onToggle={(e) => setAvancado((e.currentTarget as HTMLDetailsElement).open)}>
          <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", userSelect: "none" }}>
            Opções avançadas
          </summary>
          <div style={{ marginTop: 12 }}>
            <FormGrid cols={2}>
              {/* `<select>` ignora readOnly — travar exige `disabled`. */}
              <SelectField
                label="Adapter"
                value={form.adapter}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, adapter: e.target.value as AdminBancoInput["adapter"] })}
                options={[
                  { value: "sandbox", label: "Sandbox" },
                  { value: "ifractal", label: "iFractal" },
                ]}
              />
              {/* No modo aprovar quem decide o status e' o botao do rodape. */}
              {aprovando ? (
                <TextField label="Situação" value="Pendente → Ativo ao confirmar" readOnly style={readOnlyInputStyle} onChange={() => undefined} hint="Definida pelo botão “Confirmar e aprovar cadastro”." />
              ) : (
                <SelectField
                  label="Situação"
                  value={form.status}
                  disabled={visualizando}
                  onChange={(e) => setForm({ ...form, status: e.target.value as AdminBancoInput["status"] })}
                  options={[
                    { value: "ativo", label: "Ativo" },
                    { value: "pausado", label: "Pausado" },
                    { value: "inativo", label: "Inativo" },
                    // Ver a nota igual em prefeituras.tsx.
                    { value: "pendente", label: "Pendente (aguardando aprovação)" },
                  ]}
                />
              )}
              <TextField
                label="Scopes (vírgula)"
                {...campoEditavel(!visualizando, (form.scopes ?? []).join(","), (v) => setForm({ ...form, scopes: v.split(",").map((s) => s.trim()).filter(Boolean) }))}
              />
              <SelectField
                label="mTLS"
                value={form.mtlsHabilitado ? "1" : "0"}
                disabled={visualizando}
                onChange={(e) => setForm({ ...form, mtlsHabilitado: e.target.value === "1" })}
                options={[
                  { value: "0", label: "Desabilitado" },
                  { value: "1", label: "Habilitado" },
                ]}
              />
              <TextField
                label="URL de health check"
                {...campoEditavel(!visualizando, form.baseUrl ?? "", (v) => setForm({ ...form, baseUrl: v }))}
                type="url"
                placeholder="https://api.exemplo.com.br/health"
                hint="URL COMPLETA que deve retornar 200. Se preenchida, /averbadora/health pinga esse endpoint (uptime e latência reais)."
              />
            </FormGrid>
          </div>
        </details>

        {/* Fora do accordion, como na organização: a marca muda o que os números
            da plataforma dizem, e escondê-la atrás de "avançado" é como o tenant
            de teste passou a somar sem ninguém perceber (item 9 do PENDENCIAS). */}
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={form.tenantDeTeste ?? false}
            disabled={visualizando}
            onChange={(e) => setForm({ ...form, tenantDeTeste: e.target.checked })}
            style={{ marginTop: 3 }}
          />
          <span>
            <b style={{ color: "var(--text)" }}>Credor de teste — não conta nos números da plataforma</b>
            <br />
            Para a consignatária que a equipe usa para experimentar. Sai das contagens e do painel sempre,
            inclusive os contratos que fizer, e da lista de credores por padrão — o filtro “Credores de teste” traz
            de volta. Continua aparecendo nos seletores de convênio, rubrica e bate-carteira, marcado como teste.{" "}
            <b>Não afeta o acesso</b>: quem tem login nele continua entrando normalmente.
          </span>
        </label>

        {/* Só para banco já aprovado: grava na hora, por rota própria (auditada),
            e não pelo "Salvar" — o cadastro salvo não toca nesta marca. */}
        {initial?.id && !aprovando ? (
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--text-muted)", cursor: visualizando || definirConfirmacao.isPending ? "default" : "pointer" }}>
            <input
              type="checkbox"
              checked={confirmacaoAutomatica}
              disabled={visualizando || definirConfirmacao.isPending}
              onChange={(e) => {
                const ativa = e.target.checked;
                if (ativa && !confirm(
                  `Ligar a confirmação automática para "${initial.nome}"?\n\n` +
                    "O arquivo mensal que ela enviar passa a ser confirmado no ato, sem a averbadora aceitar a prévia, " +
                    "e grava as consignações no movimento. Até as pendências que hoje pedem justificativa passam, " +
                    "com uma declaração automática registrada na auditoria. Só o corte passado segura o arquivo.",
                )) return;
                definirConfirmacao.mutate(ativa);
              }}
              style={{ marginTop: 3 }}
            />
            <span>
              <b style={{ color: "var(--text)" }}>Confirmação automática do arquivo mensal</b>
              <br />
              Ligada, o arquivo enviado por esta consignatária é confirmado no ato, sem a averbadora aceitar a prévia.
              Rubrica ausente e queda grande passam com declaração automática, registrada na auditoria; só o corte
              passado deixa o arquivo na fila de confirmação. Vale para quem enviar: a consignatária ou a averbadora.
              {definirConfirmacao.isError ? (
                <span role="alert" style={{ display: "block", color: "var(--danger-500)" }}>
                  {definirConfirmacao.error instanceof Error ? definirConfirmacao.error.message : "Não foi possível alterar."}
                </span>
              ) : null}
            </span>
          </label>
        ) : null}

        {/* Só para banco já cadastrado: a lista vive no registro dele. */}
        {initial?.id ? (
          <CatalogoVerbasBanco bancoId={initial.id} somenteLeitura={visualizando} />
        ) : null}

        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}
        {(() => {
          // Aviso so aparece APOS o user clicar Salvar sem preencher tudo.
          if (!triedSubmit) return null;
          const faltando: string[] = [];
          if (!form.nome) faltando.push("razão social (busque o CNPJ)");
          // Mesma regra do Salvar: consignatária não tem credencial a preencher.
          if (initial?.tipo !== "consignataria") {
            if (!form.loginEmail) faltando.push("e-mail de login");
            if (!form.contatoEmail) faltando.push("e-mail de contato");
          }
          if (faltando.length === 0) return null;
          return (
            <div style={{ fontSize: 12, color: "var(--gold-500)", padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 30%, transparent)" }}>
              ⚠ Falta preencher: {faltando.join(", ")}.
            </div>
          );
        })()}
        {/* Visualizar nao submete nada — so fecha. */}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>{visualizando ? "Fechar" : "Cancelar"}</Button>
          {visualizando ? null : (
          <Button
            type="button"
            variant={aprovando ? "success" : "primary"}
            disabled={save.isPending}
            onClick={() => {
              // Consignatária não tem acesso (a API recusa login, senha e escopo
              // nela): exigir os três travava o Salvar para sempre, e a natureza
              // jurídica da ASSALCE nunca podia ser definida pela tela (#269).
              const consignataria = initial?.tipo === "consignataria";
              const faltando =
                !form.nome
                || (!consignataria && (
                  !form.loginEmail
                  || !form.contatoEmail
                ));
              if (faltando) {
                setTriedSubmit(true);
                return;
              }
              save.mutate();
            }}
          >
            {aprovando
              ? (save.isPending ? "Aprovando..." : "Confirmar e aprovar cadastro")
              : (save.isPending ? "Salvando..." : "Salvar")}
          </Button>
          )}
        </FormActions>
      </div>

      {/* So alcancavel pelo modo aprovar — ver o botao no bloco de dados oficiais. */}
      {confirmandoEdicao ? (
        <ConfirmarEdicaoManualModal
          onConfirm={() => { setEdicaoManual(true); setConfirmandoEdicao(false); }}
          onClose={() => setConfirmandoEdicao(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * CATÁLOGO DE VERBAS DO BANCO — biblioteca reutilizável.
 *
 * Só aparece para banco já cadastrado: a lista vive no registro dele e precisa
 * de um id. Salva por endpoint próprio, separado do resto do formulário, porque
 * substitui a lista inteira.
 *
 * O texto de apoio não é decoração. A confusão que esta feature existe para
 * desfazer é justamente achar que declarar a verba em algum lugar já classifica
 * a folha — e o catálogo do banco é exatamente o lugar onde isso NÃO acontece.
 */
function CatalogoVerbasBanco({ bancoId, somenteLeitura }: { bancoId: number; somenteLeitura: boolean }) {
  const qc = useQueryClient();
  const [linhas, setLinhas] = useState<AdminVerbaCatalogoInput[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  const q = useQuery({
    queryKey: ["admin", "bancos", bancoId, "verbas"],
    queryFn: () => atlas.admin.listVerbasBanco(bancoId),
  });

  const atuais: AdminVerbaCatalogoInput[] =
    linhas ??
    (q.data?.verbas ?? []).map((v) => ({
      codigo: v.codigoOriginal,
      descricao: v.descricao,
      produtoSugerido: v.produtoSugerido,
      situacao: v.situacao,
      observacoes: v.observacoes,
    }));

  const salvar = useMutation({
    mutationFn: () => atlas.admin.replaceVerbasBanco(bancoId, atuais),
    onSuccess: () => {
      setErro(null);
      setSalvo(true);
      setLinhas(null);
      qc.invalidateQueries({ queryKey: ["admin", "bancos", bancoId, "verbas"] });
    },
    onError: (e: unknown) => {
      setSalvo(false);
      setErro(e instanceof Error ? e.message : "não foi possível salvar o catálogo");
    },
  });

  function alterar(i: number, campo: keyof AdminVerbaCatalogoInput, valor: string) {
    const copia = [...atuais];
    copia[i] = { ...copia[i]!, [campo]: valor };
    setLinhas(copia);
    setSalvo(false);
  }

  return (
    <details>
      <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", userSelect: "none" }}>
        Catálogo de verbas
      </summary>
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ fontSize: 12, color: "var(--text-dim)", margin: 0, lineHeight: 1.5 }}>
          Esta lista é uma <strong>biblioteca</strong>. A verba só passa a valer quando um convênio a
          declarar para uma prefeitura — é lá que se define o produto e desde qual competência.
          Nada aqui muda a folha de nenhum município.
        </p>

        {q.isLoading ? <div style={{ fontSize: 13, color: "var(--text-dim)" }}>Carregando…</div> : null}

        {atuais.map((v, i) => (
          <FormGrid cols={2} key={`verba-${i}`}>
            <TextField
              label="Código"
              value={v.codigo}
              onChange={(e) => alterar(i, "codigo", e.target.value)}
              disabled={somenteLeitura}
              maxLength={4}
              hint="Até 4 caracteres — é o que a coluna do arquivo de desconto comporta."
            />
            <TextField
              label="Descrição"
              value={v.descricao}
              onChange={(e) => alterar(i, "descricao", e.target.value)}
              disabled={somenteLeitura}
            />
            <SelectField
              label="Produto sugerido"
              value={v.produtoSugerido ?? ""}
              disabled={somenteLeitura}
              onChange={(e) => alterar(i, "produtoSugerido", e.target.value)}
              options={[
                { value: "", label: "— sem sugestão —" },
                { value: "EMPRESTIMO", label: "Empréstimo" },
                { value: "CARTAO_CONSIGNADO", label: "Cartão consignado" },
                { value: "CARTAO_BENEFICIOS", label: "Cartão benefício" },
              ]}
              hint="Sugestão para o convênio. Quem decide o produto que vale é a declaração lá."
            />
            <SelectField
              label="Situação"
              value={v.situacao ?? "ativa"}
              disabled={somenteLeitura}
              onChange={(e) => alterar(i, "situacao", e.target.value)}
              options={[
                { value: "ativa", label: "Ativa" },
                { value: "inativa", label: "Inativa (sai das candidatas)" },
              ]}
              hint="Inativar não apaga: convênios que já a declararam seguem valendo."
            />
          </FormGrid>
        ))}

        {erro ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{erro}</div> : null}
        {salvo ? <div style={{ color: "var(--success-500)", fontSize: 13 }}>Catálogo salvo.</div> : null}

        {somenteLeitura ? null : (
          <FormActions>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setLinhas([...atuais, { codigo: "", descricao: "", produtoSugerido: null, situacao: "ativa" }]);
                setSalvo(false);
              }}
            >
              Adicionar verba
            </Button>
            <Button type="button" disabled={salvar.isPending} onClick={() => salvar.mutate()}>
              {salvar.isPending ? "Salvando…" : "Salvar catálogo"}
            </Button>
          </FormActions>
        )}
      </div>
    </details>
  );
}

// LimparBaseBancosModal removida 27/07/2026 — codigo morto desde que o
// botao trigger foi retirado. Reintroduzir junto com o botao se o fluxo
// voltar a ser necessario.

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
  zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 640, width: "calc(100% - 32px)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  margin: "auto",
};
